/* MINA 6-Pack — AC-6. Strip the 12-month selling plan from Loop's PDP frequency control.
 *
 * THE LEAK, confirmed live 2026-09-09. Loop allocates a plan per VARIANT, never per quantity. The
 * 12-month plans exist to price a 6-Pack (two 3-Packs at 18% / 13% off), but they are allocated to
 * the 3-Pack variants, so Loop offers them to anyone buying ONE 3-Pack — at the 6-Pack per-unit
 * rate on half the goods. $57.39 instead of $66.49 on Fluoride, a $9.10 loss every delivery;
 * $49.58 instead of $54.14 on Standard, $4.56. Spec §5.2: "Suppressing it there is required, not
 * optional (AC-6)."
 *
 * WHY THIS IS ITS OWN FILE, LOADED GLOBALLY, RATHER THAN PART OF THE PICKER.
 * The obvious home is assets/six-pack-picker.js, which already knows the plan ids and already
 * suppresses Loop's whole block while the 6-Pack is selected. It was rejected deliberately
 * (Sanjeet, 2026-09-09), because that script is loaded from inside the picker's readiness gate --
 * snippets/six-pack-picker.liquid only emits its <script> when sp_resolved > 0. The leak, however,
 * exists whenever the PLANS ARE MAPPED, which is a completely different condition. Every gate
 * failure that is not "the plans are unmapped" -- an out-of-scope market, a renamed product option,
 * an unavailable variant, a metafield that parses but resolves no panel -- would silently switch
 * AC-6 off while leaving the plans live and billing. The protection has to outlive the feature it
 * protects.
 *
 * It still reads the SAME metafield, via the same already-global Liquid in layout/theme.liquid that
 * feeds the drawer ("the drawer is global but the metafield lives on the product, so read it by
 * handle here"). That block is not gated on 6-Pack readiness, which is exactly why it can serve as
 * this file's independent source.
 *
 * FAILS OPEN, BY DECISION (Sanjeet, 2026-09-09) — and it is the OPPOSITE of the drawer's rule, on
 * purpose. assets/six-pack-cart.js hides its upgrade control on `promotesSixPack || ids.length === 0`,
 * reasoning that "a leak costs real money every delivery, a missed upsell costs nothing measurable".
 * That comparison inverts here. A false positive on the PDP is not a missed upsell: it is a shopper
 * who cannot buy the 2-month or 6-month subscription AT ALL, on the primary buy path. Worse, "no id
 * readable" is the NORMAL state for the first frames of every page load and after every variant
 * change, because Loop hydrates asynchronously (assets/component-purchase-picker.js polls 40 x 50ms
 * waiting for it) — so a fail-closed sweep would blank the frequency control on essentially every
 * page view. We therefore act only on POSITIVE identification, and change nothing otherwise.
 *
 * Residual risk, stated rather than papered over: if Loop stops exposing plan ids in option values
 * this file silently no-ops and the leak returns, mitigated only by a console warning; and the
 * whole thing is client-side, so a crafted /cart/add POST carrying selling_plan bypasses it. Spec
 * §5.2 already accepts that second one knowingly for the drawer surface.
 */
(() => {
  /* Plan ids as STRINGS. six-pack-cart.js keeps two forms on purpose -- Numbers for id comparison,
   * Strings for digits scraped out of markup -- and mixing them makes indexOf() silently never
   * match. Here every comparison is against an option's `value` attribute, so String throughout.
   *
   * The digit test is not paranoia: it is what makes a hand-mangled or half-rendered attribute
   * fail closed on the DATA while the sweep itself fails open on the DOM. A non-numeric entry is
   * dropped rather than trusted. */
  const cfgEl = document.querySelector('script[data-loop-guard-plans]');
  const PLANS = ((cfgEl && cfgEl.getAttribute('data-loop-guard-plans')) || '')
    .split(',')
    .map((s) => s.trim())
    .filter((s) => /^\d+$/.test(s));
  if (!PLANS.length) return;

  /* `.loop-widget-sp-selector` is the stable, prefixed class on Loop's frequency <select>, read off
   * the live page 2026-09-09. (Precision, added 2026-09-18: the cart does not read this <select>
   * directly -- Loop mirrors its value into a hidden input in the product form on `change`. Removing
   * an option still prevents the purchase, because an option that is not there cannot be chosen,
   * and a strip that removes the SELECTED option fires `change` so Loop's hidden input follows.) Do NOT key on the element's id: it is
   * `loop-widget-sp-selector-dropdown-<GROUP_ID>`, and the group id is a hash that changes whenever
   * the groups are restructured -- which already happened once here, when renaming the plans merged
   * three groups into one. Same rule as the drawer's `.upcart-product-item` versus the hashed
   * `styles_ProductRow__product__` beside it.
   *
   * It is a real <select name="selling_plan">, a form control the add-to-cart actually reads, so
   * removing an option genuinely prevents the purchase. This is not a cosmetic hide. */
  const SELECT_SEL = 'select.loop-widget-sp-selector';

  let warned = false;
  const warnOnce = (msg) => {
    if (warned) return;
    warned = true;
    /* One warning per page load, never per sweep: the observer below can fire many times a second
     * and a per-sweep log would bury the console and read as a fault of its own. */
    console.warn('[MINA 6-Pack AC-6] ' + msg);
  };

  function sweep() {
    document.querySelectorAll(SELECT_SEL).forEach((sel) => {
      const opts = Array.from(sel.options);
      if (!opts.length) return;

      /* Matched on the option's VALUE, which on this widget is the bare plan id -- never on its
       * TEXT. Both 12-month plans are named identically, "Deliver every year", so text cannot tell
       * Fluoride from Standard. And a looser text match is actively dangerous: the drawer sweep
       * records that /annual|12 month|year/i would also strip the live SF Sub-Bundle plan, whose
       * name contains "Annual", from an unrelated product. */
      const doomed = opts.filter((o) => PLANS.indexOf(String(o.value || '').trim()) !== -1);
      if (!doomed.length) {
        /* Nothing to strip. If this <select> was shown as text earlier but no longer holds exactly
         * one option -- Loop refreshed its options in place rather than rebuilding the wrapper --
         * give the shopper the dropdown back instead of a hidden control behind stale text. */
        if (opts.length !== 1) restoreDropdown(sel);
        /* Still one option, already shown as text: keep the text true to it, in case Loop swapped
         * that one option for another in place (review 2026-09-18; not seen in Loop's code today).
         * showAsText() writes nothing when the text already matches, so this cannot loop. */
        else if (hasText(sel)) showAsText(sel);
        return;
      }

      /* EVERY option is a 6-Pack plan => we matched the wrong element. Since the 2026-09-08 rename
       * merged all four plans into one group, a legitimate frequency control on this product always
       * contains at least one non-6-Pack option. Emptying the control would leave a dead <select>,
       * which this design forbids everywhere. Bail and say so. */
      if (doomed.length === opts.length) {
        warnOnce('every option on a frequency control is a 6-Pack plan — refusing to empty it. '
          + 'Selector is probably matching something it should not: ' + SELECT_SEL);
        return;
      }

      /* DO NOT PORT THE DRAWER'S `opt.selected` GUARD. There it protects a real subscriber's own
       * current plan from vanishing out of their dropdown. This is a PURCHASE control -- nobody is
       * subscribed to anything yet -- so there is nothing to protect, and skipping a selected
       * option would leave the leak open on precisely the case that matters: a shopper who already
       * clicked "Deliver every year" before this ran.
       *
       * Removing the selected <option> leaves the control's selection undefined and Loop's own
       * state stale, so re-point it and tell Loop, rather than leaving a control whose displayed
       * price and submitted plan disagree. Every surviving option is safe by construction, the
       * doomed ones having just been removed. */
      const wasSelected = doomed.some((o) => o.selected);
      doomed.forEach((o) => o.remove());

      if (wasSelected && sel.options.length) {
        sel.selectedIndex = 0;
        sel.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (sel.options.length === 1) showAsText(sel);
    });
  }

  /* ONE PLAN LEFT -> SHOW IT AS TEXT, the way Loop itself shows a single plan (Sanjeet, 2026-09-18).
   *
   * Why there is a dropdown at all: since the 2026-09-08 rename merged every plan into ONE group, a
   * 3-Pack is allocated two plans -- 6-month and 12-month -- so Loop draws its <select>. Stripping
   * the 12-month plan above leaves a dropdown with a single choice, while the Single (one plan) shows
   * plain "Frequency: Deliver Every 2 Months". This store has Loop's
   * `showPlanSelectorAsTextIfOnePlanAvailable` preference ON -- that is why the Single shows text --
   * so rendering text here is what Loop itself would do had it been given one plan. Only a <select>
   * this sweep has just stripped reaches this function, so no other control on the store changes.
   *
   * The markup is Loop's own text template, copied from its widget (generateSpSelectorAsText, Loop
   * extension loop-subscriptions-261):
   *   div.loop-widget-sp-selector-label-as-text.loop-widget-left-padding-0
   *     span.loop-widget-sp-selector-as-text-label  "<label>: "
   *     span.loop-widget-sp-option                   "<plan>"
   * It deliberately WEARS LOOP'S CLASS NAMES -- an exception to this project's own-every-element rule,
   * made because it must look identical to the Single's text, which Loop's CSS and theme.css:11136-
   * 11144 style through exactly these classes. Checked 2026-09-18 that nothing reads them: no script
   * in this repo selects any of them, and Loop's own queries are `.loop-widget-sp-selector` (the
   * <select>'s exact class, which this div does not carry) and `option.loop-widget-sp-option` (real
   * <option>s only, never this span). `data-mina-sp-text` is our own handle on it.
   *
   * THE <select> STAYS IN THE DOCUMENT, only hidden -- and hiding it changes nothing about what is
   * bought. The cart does NOT read this <select>: on the MINA page Loop's widget sits outside the
   * product form, and Loop writes the chosen plan into its own hidden `selling_plan` input inside
   * that form, driven by this <select>'s change event (loop_widget.js, handleLoopWidgetSellingPlanValue;
   * traced in review 2026-09-18). The strip above already fired that change event when it moved the
   * selection, before this runs. *An earlier version of this comment said the hidden <select> itself
   * is submitted; that was the wrong reason for the right conclusion.* Its label and box are hidden
   * with the `hidden` attribute, which
   * theme.css:192 enforces with !important. That is an ATTRIBUTE mutation, and the observer below
   * does not watch attributes; inserting the text div is a childList mutation, and the next sweep
   * finds nothing to strip and so never reaches here again -- the same fixpoint argument as above.
   *
   * FAILS OPEN like the rest of this file: no recognisable wrapper means the dropdown is left as it
   * is -- still correct, still leak-free, just not the text look. Text goes in through textContent
   * only; the plan name comes from Loop's markup and is never parsed as HTML. */
  const TEXT_MARK = 'data-mina-sp-text';

  function showAsText(sel) {
    const wrapper = sel.closest('.loop-widget-sp-selector-wrapper');
    const box = sel.closest('.loop-widget-sp-selector-container');
    if (!wrapper || !box || !wrapper.contains(box)) return;

    const labelEl = wrapper.querySelector(':scope > .loop-widget-sp-selector-label');
    const label = labelEl ? labelEl.textContent.trim() : '';
    const plan = (sel.options[0].textContent || '').trim();
    if (!plan) return;

    /* Hiding is an attribute write, which the observer ignores, so it is always safe to repeat. */
    if (labelEl) labelEl.hidden = true;
    box.hidden = true;

    /* IDEMPOTENT: when the text already reads right, write NOTHING to the tree. This function is
     * reachable from a sweep that stripped nothing, and any childList write here would re-fire the
     * observer into another sweep -- so the no-op is the terminator. */
    const want = (label ? label + ': ' : '') + plan;
    let text = wrapper.querySelector(':scope > [' + TEXT_MARK + ']');
    if (text && text.textContent === want) return;
    if (!text) {
      text = document.createElement('div');
      text.className = 'loop-widget-sp-selector-label-as-text loop-widget-left-padding-0';
      text.setAttribute(TEXT_MARK, '');
      wrapper.appendChild(text);
    }
    text.textContent = '';
    if (label) {
      const l = document.createElement('span');
      l.className = 'loop-widget-sp-selector-as-text-label';
      l.textContent = label + ': ';
      text.appendChild(l);
    }
    const p = document.createElement('span');
    p.className = 'loop-widget-sp-option';
    p.textContent = plan;
    text.appendChild(p);
  }

  function hasText(sel) {
    const wrapper = sel.closest('.loop-widget-sp-selector-wrapper');
    return !!(wrapper && wrapper.querySelector(':scope > [' + TEXT_MARK + ']'));
  }

  /* The undo of showAsText(), for a <select> that no longer holds exactly one option. Loop's own
   * templates rebuild the whole wrapper on every change, which drops our text with it, so this is
   * expected never to run -- it exists so that an in-place options refresh can never leave a real
   * choice hidden. Removing the text is a childList mutation; the next sweep finds no text, nothing
   * to strip, and stops. */
  function restoreDropdown(sel) {
    const wrapper = sel.closest('.loop-widget-sp-selector-wrapper');
    if (!wrapper) return;
    const text = wrapper.querySelector(':scope > [' + TEXT_MARK + ']');
    if (!text) return;
    text.remove();
    const labelEl = wrapper.querySelector(':scope > .loop-widget-sp-selector-label');
    const box = sel.closest('.loop-widget-sp-selector-container');
    if (labelEl) labelEl.hidden = false;
    if (box) box.hidden = false;
  }

  /* Loop re-renders this control constantly and none of it is observable through the theme's own
   * events, so an observer is required rather than merely convenient:
   *   - the widget does not exist at script time (component-purchase-picker.js polls up to ~2s for
   *     it, "40 x 50ms", before giving up);
   *   - it is rebuilt AFTER variant:change, not before -- that same file awaits ~60ms around the
   *     dispatch -- so anything hooked to variant:change sweeps the OLD markup;
   *   - and no variant event fires at all on the One-time <-> Subscribe toggle, which is the exact
   *     moment the frequency control appears. That is how this leak was found by hand.
   *
   * The pattern is snippets/hide-internal-cart-properties.liquid's, which already solves "react to
   * an app re-rendering its own markup" in this theme: observe documentElement, coalesce to one
   * pass per frame.
   *
   * WHY THIS CANNOT LOOP FOREVER, since the sweep mutates inside what it observes. IDEMPOTENCE is
   * the terminator, not the rAF -- the rAF only rate-limits. Pass 1 removes an option, which is a
   * childList mutation, which re-fires the observer; pass 2 matches nothing, mutates nothing, and
   * the cascade stops. `attributes` is deliberately absent from the config: with it, any future
   * hide-by-class variant of this sweep would have no fixpoint at all. */
  let queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      sweep();
    });
  }

  new MutationObserver(schedule).observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('variant:change', schedule);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sweep);
  }
  sweep();
})();
